﻿<script setup lang="ts">
    import { computed, inject, ref, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { AccountAccess } from '@account/Account.access';
    import ThemesViewAndSelect from './ThemesViewAndSelect.vue';
    import ThemesListPreview from './ThemesListPreview.vue';
    import { getDesignTheme } from './Themes.access';
    import type { DesignTheme } from './ThemesTypes';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const accountAccess: AccountAccess = injectorAccess.injector.get(AccountAccess);

    const props = defineProps<{
        themeId?: string | null;
        useDefaultDesignTheme?: boolean;
    }>();

    const emit = defineEmits<{
        (ev: 'update:themeId', value: string | null): void;
        (ev: 'update:useDefaultDesignTheme', value: boolean): void;
        (ev: 'update:selectedTheme', id: string | null, theme: DesignTheme | null): void;
    }>();

    const showModal = ref(false);
    const selectedTheme = ref<DesignTheme | null>(null);
    const defaultTheme = ref<DesignTheme | null>(null);

    const selectedThemeId = computed(() => {
        return props.themeId || null;
    });

    const defaultThemeId = computed(() => {
        return accountAccess.state.currentAccount?.DefaultDesignThemeId || null;
    });

    const useDefaultDesignTheme = computed(() => {
        return props.useDefaultDesignTheme ?? false;
    });

    const goToThemeEditor = computed(() => {
        return selectedThemeId.value ? '/Account/Themes?themeId=' + selectedThemeId.value : '/Account/Themes';
    });

    const activeThemeForPreview = computed(() => {
        if (useDefaultDesignTheme.value) {
            return defaultTheme.value || selectedTheme.value;
        }

        return selectedTheme.value;
    });

    const previewId = computed(() => {
        return activeThemeForPreview.value?.DesignThemeId || 'selected-theme-preview';
    });

    const loadSelectedTheme = async (themeId: string | null) => {
        if (!themeId) {
            selectedTheme.value = null;
            return;
        }

        selectedTheme.value = await getDesignTheme(themeId);
        emit('update:selectedTheme', themeId, selectedTheme.value);
    };

    watch(
        () => props.themeId,
        async (newId) => {
            await loadSelectedTheme(newId || null);
        },
        { immediate: true }
    );

    watch(
        [defaultThemeId, useDefaultDesignTheme],
        async ([newDefaultThemeId, shouldUseDefault]) => {
            if (!shouldUseDefault || !newDefaultThemeId) {
                defaultTheme.value = null;
                emit('update:useDefaultDesignTheme', false);

                if (selectedTheme.value) {
                    emit('update:selectedTheme', selectedThemeId.value, selectedTheme.value);
                } else {
                    emit('update:selectedTheme', null, null);
                }

                return;
            }

            defaultTheme.value = await getDesignTheme(newDefaultThemeId);
            emit('update:selectedTheme', newDefaultThemeId, defaultTheme.value);
        },
        { immediate: true }
    );

    const removeTheme = () => {
        emit('update:themeId', null);
        emit('update:selectedTheme', null, null);
        selectedTheme.value = null;
    };

    const themeSelected = (themeId: string, theme: DesignTheme) => {
        selectedTheme.value = theme;
        emit('update:themeId', themeId);
        emit('update:useDefaultDesignTheme', false);
        emit('update:selectedTheme', themeId, theme);
        showModal.value = false;
    };

    const updateUseDefaultDesignTheme = (newValue: boolean) => {
        emit('update:useDefaultDesignTheme', newValue);
    };

    const closeModal = () => {
        showModal.value = false;
    };
</script>

<template>
    <div v-feature="'Account.Themes'">
        <h3 class="h4">{{ $Resources.DesignThemeHeading }}</h3>

        <div v-if="activeThemeForPreview">
            <h3 class="h5 d-flex flex-wrap gap-xs align-items-baseline">
                <span class="m-xs-r">{{ activeThemeForPreview.Name || $Resources.NoNamePlaceholder }}</span>

                <a v-if="selectedTheme?.ThemeBelongsToAccount" :href="goToThemeEditor" class="btn btn-default btn-xs">
                    {{ $Resources.EditButton }}
                </a>

                <button v-if="!useDefaultDesignTheme" type="button" class="btn btn-default btn-xs" @click="removeTheme">
                    {{ $Resources.RemoveButton }}
                </button>
            </h3>

            <p>{{ activeThemeForPreview.Description }}</p>

            <div class="pe-none m-lg-b">
                <ThemesListPreview :css="activeThemeForPreview.ThemeCss" :id="previewId" />
            </div>
        </div>

        <div class="form-group d-flex gap-lg flex-wrap align-items-center">
            <button type="button" class="btn btn-default" :disabled="useDefaultDesignTheme" @click="showModal = true">{{ $Resources.SelectThemeLabel }}</button>

            <FormCheckable
                :checked="useDefaultDesignTheme"
                :label="$Resources.UseAccountDefaultThemeLabel"
                :switchStyle="SwitchStyle.Standard"
                :disabled="!defaultThemeId"
                @update:modelValue="(newValue: boolean) => updateUseDefaultDesignTheme(newValue)" />
        </div>
    </div>

    <Modal :title="$Resources.SelectThemeLabel" :show="showModal" @close="closeModal()" size="lg">
        <template #content>
            <ThemesViewAndSelect :currentThemeId="selectedThemeId || ''" @themeSelected="(themeId, theme) => themeSelected(themeId, theme)" />
        </template>
    </Modal>
</template>
